import { Injectable } from '@angular/core';
import { FeatureFlag, requireFeatureEnabled } from '../feature-flags';
import * as CryptoJS from 'crypto-js';
import { LogService } from '../baseServices/logging/log.service';
import { environment } from '../../environments/environment';

/**
 * Keys stored in DriverServiceIssuance table (RecordId=1)
 */
export interface StoredKeys {
  publicKey: string;                    // SPKI public key (standard base64) for API-side ECDSA validation
  encryptedPrivateKey: string;          // AES-encrypted private key (crypto-js format)
  keysCreatedAt: number;                // Timestamp
}

/**
 * Driver Session Cryptography Service
 * 
 * Manages ECDSA P-256 key pair generation, encryption, and storage for Driver Session QR JWT signing.
 * Keys are regenerated on every login (every 8 hours with auth timeout) for better security.
 * Private keys are encrypted with AES-256-CBC using environment encryption key (same pattern as LocalStorageService).
 * Keys are stored in DriverServiceIssuance singleton table (RecordId=1) alongside issuance data.
 * 
 * Security Model:
 * - Private key: encrypted at rest (AES-256-CBC via crypto-js), decrypted only when signing
 * - Encryption key: from environment config (consistent with LocalStorageService pattern)
 * - Public key: sent to API for server-side validation (embedded in signed issuance payload)
 * - Algorithm: ECDSA P-256 with SHA-256 (ES256 in JWT spec)
 * - Key rotation: Fresh keys generated every login (8-hour auth timeout)
 */

@Injectable({
  providedIn: 'root'
})
export class DriverSessionCryptographyService {
  private static readonly ALGO = 'ECDSA';
  private static readonly CURVE = 'P-256';
  private static readonly HASH = 'SHA-256';
  
  /**
   * AES encryption key from environment config.
   * Used to encrypt/decrypt private ECDSA keys for storage in IndexedDB.
   */
  private readonly encryptionKey = environment.storageEncryptionKey;
  
  constructor(
    private logService: LogService
  ) {}
  
  /**
   * Generate ECDSA P-256 key pair and encrypt the private key.
   * Pure key material generation - does not touch storage. Callers that need
   * to persist alongside other data (e.g. issuance) should use this together
   * with their own atomic write, rather than calling `generateAndStoreKeys()`.
   */
  async generateKeyMaterial(): Promise<StoredKeys> {
    requireFeatureEnabled(FeatureFlag.DriverSession);
    // Generate ECDSA P-256 key pair
    const keyPair = await crypto.subtle.generateKey(
      {
        name: DriverSessionCryptographyService.ALGO,
        namedCurve: DriverSessionCryptographyService.CURVE
      },
      true, // Extractable (need to export for storage)
      ['sign', 'verify']
    );
    
    // Export public key as SPKI DER (standard base64), matching the WebApi import path.
    const publicKeySpki = await crypto.subtle.exportKey('spki', keyPair.publicKey);
    const publicKeyBase64 = this.bytesToBase64(new Uint8Array(publicKeySpki));

    // Export private key as JWK (only ever imported locally for signing, never transmitted)
    const privateKeyJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey);
    const privateKeyJson = JSON.stringify(privateKeyJwk);
    
    // Encrypt private key with AES (crypto-js, same pattern as LocalStorageService)
    const encryptedPrivateKey = this.encryptPrivateKey(privateKeyJson);
    
    return {
      publicKey: publicKeyBase64,
      encryptedPrivateKey,
      keysCreatedAt: Date.now()
    };
  }

  /**
   * Decrypt the given encrypted private key material and import it as a CryptoKey.
   */
  async getDecryptedPrivateKey(stored: StoredKeys): Promise<CryptoKey> {
    requireFeatureEnabled(FeatureFlag.DriverSession);
    try {
      // Decrypt private key (crypto-js AES-256-CBC)
      const privateKeyJson = this.decryptPrivateKey(stored.encryptedPrivateKey);
      
      // Validate decryption succeeded
      if (!privateKeyJson || privateKeyJson.trim() === '') {
        throw new Error('Decryption failed - invalid encryption key or corrupted data');
      }
      
      // Import as CryptoKey
      const privateKeyJwk = JSON.parse(privateKeyJson);
      const privateKey = await crypto.subtle.importKey(
        'jwk',
        privateKeyJwk,
        {
          name: DriverSessionCryptographyService.ALGO,
          namedCurve: DriverSessionCryptographyService.CURVE
        },
        false, // Not extractable (used only for signing)
        ['sign']
      );
      
      this.logService.event('DriverSessionSigning.PrivateKeyRetrieved', {
        keysAge: Date.now() - stored.keysCreatedAt
      });
      
      return privateKey;
      
    } catch (err) {
      this.logService.error('DriverSessionSigning.KeyRetrievalFailed', err as any);
      throw new Error('Failed to retrieve signing keys');
    }
  }
  
  // --- Private Methods ---
  
  /**
   * Encrypt private key using environment AES-256-CBC key (crypto-js, same pattern as LocalStorageService).
   */
  private encryptPrivateKey(privateKeyJson: string): string {
    return CryptoJS.AES.encrypt(privateKeyJson, this.encryptionKey).toString();
  }
  
  /**
   * Decrypt private key using environment AES-256-CBC key (crypto-js, same pattern as LocalStorageService).
   * Returns empty string if decryption fails (wrong key or corrupted data).
   */
  private decryptPrivateKey(encrypted: string): string {
    return CryptoJS.AES.decrypt(encrypted, this.encryptionKey).toString(CryptoJS.enc.Utf8);
  }

  private bytesToBase64(bytes: Uint8Array): string {
    return btoa(String.fromCharCode(...bytes));
  }
}
